import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import PreviewGlassyDashboard from "@/pages/PreviewGlassyDashboard";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import FinanceDashboard from "@/pages/FinanceDashboard";
import DashboardSwitch from "@/pages/DashboardSwitch";
import Prospects from "@/pages/Prospects";
import Kanban from "@/pages/Kanban";
import Team from "@/pages/Team";
import Targets from "@/pages/Targets";
import Settings from "@/pages/Settings";
import NotificationCenter from "@/pages/NotificationCenter";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Activities from "@/pages/Activities";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import Quotations from "@/pages/Quotations";
import Invoices from "@/pages/Invoices";
import SalesReport from "@/pages/reports/SalesReport";
import TeamReport from "@/pages/reports/TeamReport";
import ProfitLossReport from "@/pages/reports/ProfitLossReport";
import Layout from "@/components/app/Layout";
import { Toaster } from "@/components/ui/sonner";

function Protected({ children, ownerOnly, blockFinance, allow }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (ownerOnly && user.role !== "owner" && user.role !== "sales_director") return <Navigate to="/dashboard" replace />;
  if (blockFinance && user.role === "finance") return <Navigate to="/dashboard" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

/** Finance sees an AR-focused dashboard instead of the sales dashboard. */
function DashboardRoute() {
  const { user } = useAuth();
  if (user?.role === "finance") return <FinanceDashboard />;
  if (user?.role === "owner" || user?.role === "sales_director") return <DashboardSwitch />;
  return <Dashboard />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/preview/glassy-dashboard" element={<PreviewGlassyDashboard />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route path="/prospects" element={<Protected blockFinance><Prospects /></Protected>} />
        <Route path="/kanban" element={<Protected blockFinance><Kanban /></Protected>} />
        <Route path="/aktivitas" element={<Protected blockFinance><Activities /></Protected>} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/quotations" element={<Quotations />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/purchase-orders" element={<PurchaseOrders />} />
        <Route path="/profile" element={<Navigate to="/settings?tab=profile" replace />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/notifications" element={<NotificationCenter />} />
        <Route path="/team" element={<Protected ownerOnly><Team /></Protected>} />
        <Route path="/targets" element={<Protected ownerOnly><Targets /></Protected>} />
        <Route path="/reports/sales" element={<Protected blockFinance><SalesReport /></Protected>} />
        <Route path="/reports/team" element={<Protected blockFinance><TeamReport /></Protected>} />
        <Route path="/reports/laba-rugi" element={<Protected allow={["owner", "finance", "sales_director"]}><ProfitLossReport /></Protected>} />
      </Route>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
